/**
 * CMP同意ログ送信
 *
 * 通常時はfetchを使用し、ページ離脱時（visibilityState === 'hidden'）は
 * navigator.sendBeacon()にフォールバックする。
 * 送信失敗はリトライせず黙って無視する。
 */

/**
 * 同意ログをサーバーに送信する
 * @param {string} url - 送信先URL
 * @param {Object} data - { consent_id, categories, action }
 */
export function sendConsentLog(url, data) {
  if (!url) return;

  const json = JSON.stringify(data);

  // ページ離脱中はsendBeaconを使用（fetchはキャンセルされる可能性がある）
  if (document.visibilityState === 'hidden' && navigator.sendBeacon) {
    const blob = new Blob([json], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
    return;
  }

  // 通常時はfetchを使用
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: json,
    keepalive: true
  }).catch(() => {}); // エラーは黙って無視
}

/**
 * アクセス元国コードをサーバーから取得する
 *
 * バナー表示をブロックしないよう短いタイムアウトを設けるが、リクエスト自体は
 * 中断せず裏で完走させる。タイムアウト後に判定が完了した場合はonLateResultに
 * 通知するので、呼び出し側はCookieに保存して次回ページロードから利用できる
 * （中断・破棄すると低速回線のユーザーが毎ページロードでタイムアウト待ちと
 * 無駄なリクエストを繰り返すため）。
 * エラーはスローせずconsole.warnのみ。
 *
 * @param {string} url - リージョン判定APIのURL
 * @param {number} [timeoutMs=500] - タイムアウト（ミリ秒）
 * @param {Function} [onLateResult] - タイムアウト後に判定が完了した場合のコールバック
 *   （{ country, resolved: true } を受け取る）
 * @returns {Promise<{country: string|null, resolved: boolean}>}
 *   resolved: 判定が完了したか（country=nullでも判定完了=trueがあり得る。
 *   タイムアウト/エラー時はfalseで、呼び出し側はCookieを更新せず次回再判定する）
 */
export function fetchRegion(url, timeoutMs = 500, onLateResult = null) {
  if (!url) return Promise.resolve({ country: null, resolved: false });

  const request = fetch(url, { credentials: 'same-origin' })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((data) => ({
      country: data && typeof data.country === 'string' && data.country ? data.country : null,
      resolved: true
    }))
    .catch((e) => {
      console.warn('[CMP] Failed to fetch region:', e);
      return { country: null, resolved: false };
    });

  let timedOut = false;
  let timer = null;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => {
      timedOut = true;
      resolve({ country: null, resolved: false });
    }, timeoutMs);
  });

  request.then((result) => {
    clearTimeout(timer);
    if (timedOut && result.resolved && onLateResult) onLateResult(result);
  });

  return Promise.race([request, timeout]);
}
